import { useChatBot } from "@jugl-web/domain-resources/chat-bot";
import { Tooltip } from "@jugl-web/ui-components";
import {
  Avatar,
  PlainButton,
  Popover,
} from "@jugl-web/ui-components/cross-platform";
import { cx, useAppVariant, useTranslations } from "@jugl-web/utils";
import { HELP_CENTER_URL } from "@jugl-web/utils/consts";
import { useUserAuth } from "@web-src/modules/auth/providers/UserAuthProvider/useUserAuth";
import { useNavigation } from "@web-src/modules/navigation/hooks/useNavigation";
import { useUserStatus } from "@web-src/modules/people/hooks/useUserStatus";
import { useTheme } from "@web-src/themes/useTheme";
import { useMe } from "../../../../hooks/useMe";
import { ReactComponent as ChevronRightIcon } from "./assets/chevron-right.svg";
import { ReactComponent as HelpCenterIcon } from "./assets/help-center-icon.svg";
import { ReactComponent as LogoutIcon } from "./assets/logout-icon.svg";
import { ReactComponent as SettingsIcon } from "./assets/settings-icon.svg";
import { ReactComponent as SupportChatIcon } from "./assets/support-chat-icon.svg";
import { ThemePicker } from "./components/ThemePicker";

interface ProfilePopoverProps {
  className?: string;
}

export const ProfilePopover = ({ className }: ProfilePopoverProps) => {
  const { isInitialized: isChatBotInitialized, show: showChatBot } =
    useChatBot();
  const { isCx } = useAppVariant();

  const { profile, id: meId } = useMe();
  const userStatus = useUserStatus(meId);

  const { logout } = useUserAuth();

  const { tcx } = useTheme();
  const { navigateToPage } = useNavigation();
  const { t } = useTranslations();

  return (
    <Popover
      placement="bottom"
      hasBackdrop
      backdropStyle="transparent"
      // z-index is one higher than Intercom widget
      className={cx("z-[2147483001] w-[280px] overflow-hidden py-2", className)}
      renderTrigger={({ Trigger, triggerRef, isOpen }) => (
        <Tooltip
          isDisabled={isOpen}
          renderTrigger={({ props, ref }) => (
            <div ref={ref} {...props}>
              <Trigger
                ref={triggerRef}
                as={PlainButton}
                onClick={(e) => {
                  if (isCx) {
                    e.preventDefault();
                    navigateToPage("cxWorkspaceMyAccount");
                  }
                }}
                className={tcx(
                  "easy-out flex h-8 items-center rounded-lg px-2 transition-colors duration-300",
                  {
                    light: [
                      "hover:bg-primary-700",
                      { "bg-primary-700": isOpen },
                    ],
                    dark: ["hover:bg-[#333333]", { "bg-[#333333]": isOpen }],
                    blue: [
                      "hover:bg-primary-700",
                      { "bg-primary-700": isOpen },
                    ],
                  }
                )}
              >
                <div className="flex items-center gap-1.5 truncate text-white">
                  <div className="align-center flex h-8 max-w-[200px] grow items-center justify-start truncate font-secondary text-sm font-medium">
                    {profile?.firstName}
                  </div>
                  <div className="relative">
                    <Avatar
                      className="border border-solid border-white"
                      labelClassName="text-[11px]"
                      size="sm"
                      imageUrl={profile?.avatar}
                      username={profile?.displayName || ""}
                    />
                    <span className="absolute right-0 bottom-0 h-2 w-2 rounded-full border border-solid border-white bg-gradients-success" />
                  </div>
                </div>
              </Trigger>
            </div>
          )}
        >
          {t({
            id: "profile-popover.account-settings",
            defaultMessage: "Account Settings",
          })}
        </Tooltip>
      )}
    >
      {({ onClose }) => (
        <div className="flex flex-col">
          <PlainButton
            className="mx-2 flex items-center gap-3 rounded-xl px-3 py-3 transition-colors hover:bg-grey-100"
            onClick={() => {
              navigateToPage("settingsMainSettings");
              onClose();
            }}
          >
            <Avatar
              size="lg"
              imageUrl={profile?.avatar}
              username={profile?.displayName || ""}
              className="border border-solid border-[#E0E0E0]"
            />

            <div className="flex flex-col items-start gap-0.5 overflow-hidden leading-2">
              <span className="w-full truncate font-secondary text-sm font-semibold text-dark">
                {profile?.displayName}
              </span>
              {userStatus?.formattedString && (
                <span className="font-secondary text-[13px] text-dark-700">
                  {userStatus?.formattedString}
                </span>
              )}
            </div>

            <ChevronRightIcon className="ml-auto shrink-0" />
          </PlainButton>

          <div className="my-1 h-px bg-grey-200" />

          <div className="space-y-1 px-3">
            <ThemePicker />

            {/* Settings */}
            <MenuItem
              icon={<SettingsIcon />}
              label={t({
                id: "common.settings",
                defaultMessage: "Settings",
              })}
              onClick={() => {
                navigateToPage("settingsSettings", undefined);
                onClose();
              }}
              className="px-3 py-3.5"
            />

            {/* Help Center */}
            <MenuItem
              icon={<HelpCenterIcon />}
              label={t({
                id: "main-navbar-component.help-center",
                defaultMessage: "Help Center",
              })}
              onClick={() => {
                window.open(HELP_CENTER_URL, "_blank");
                onClose();
              }}
              className="px-3 py-3.5"
            />

            {/* Support Chat */}
            {isChatBotInitialized && (
              <MenuItem
                icon={<SupportChatIcon />}
                label={t({
                  id: "main-navbar-component.support-chat",
                  defaultMessage: "Support Chat",
                })}
                onClick={() => {
                  showChatBot();
                  onClose();
                }}
                className="px-3 py-3.5"
              />
            )}
          </div>

          <div className="my-1 h-px bg-grey-200" />

          {/* Log Out */}
          <div className="px-3">
            <MenuItem
              icon={<LogoutIcon />}
              label={t({
                id: "common.logout",
                defaultMessage: "Log Out",
              })}
              onClick={() => {
                logout(true);
                onClose();
              }}
              className="px-3 py-3.5"
            />
          </div>
        </div>
      )}
    </Popover>
  );
};

interface MenuItemProps {
  icon: React.ReactNode;
  label: string;
  endSlot?: React.ReactNode;
  onClick?: () => void;
  className?: string;
}

const MenuItem = ({
  icon,
  label,
  endSlot,
  onClick,
  className,
}: MenuItemProps) => (
  <PlainButton
    className={cx(
      "flex w-full cursor-pointer items-center gap-3 rounded-xl transition-colors hover:bg-grey-100",
      className
    )}
    onClick={onClick}
  >
    <span className="shrink-0">{icon}</span>
    <span className="grow text-left font-secondary text-sm text-dark">
      {label}
    </span>
    {endSlot}
  </PlainButton>
);
